import React from 'react';
import { EMAIL_SLIDE, PAUSE_SLIDE, PLAN_GRAPH_SLIDE, PLAN_LOADER_SLIDE, PRODUCT_SLIDE, QUESTION_ANSWER_SLIDE_COMPONENT, QUESTION_MULTI_ANSWER_SLIDE_COMPONENT, START_SLIDE } from '../quiz-component-types';
import pauseCollage1Src from '../../assets/images/slide/pause-collage-1.png';
import pauseCollage2Src from '../../assets/images/slide/pause-collage-2.png';
import pauseCollage3Src from '../../assets/images/slide/pause-collage-3.png';

const PLAN_TOPIC_ANSWERS = [
    {
        text: 'Childhood',
        icon: 'house',
        id: 1
    },
    {
        text: 'School years',
        icon: 'backpack',
        id: 2
    },
    {
        text: 'The twenties',
        icon: 'bottle',
        id: 3
    },
    {
        text: 'Love and marriage',
        icon: 'bride',
        id: 4
    },
    {
        text: 'Raising you',
        icon: 'new-mother',
        id: 5
    },
    {
        text: 'The working years',
        icon: 'worker',
        id: 6
    },
    {
        text: 'These days',
        icon: 'tree',
        id: 7
    }
];

const QUIZ_SLIDES_V27 = [
    {
        id: 900,
        component: START_SLIDE,
        isNotIndexing: true,
        props: {
            heading: <><strong>Your mom’s</strong> whole <strong>life story</strong>, saved in one place</>,
            subheading: "Take the 2-minute quiz to personalize the subscription we'll build for her",
            ageQuestion: {
                id: 901,
                category: 'Her & you',
                question: 'How old is she?',
                label: 'Start by selecting her age',
                answers: [
                    {
                        text: '80+',
                        id: 7
                    },
                    {
                        text: '70–79',
                        id: 6
                    },
                    {
                        text: '60–69',
                        id: 5
                    },
                    {
                        text: 'Under 60',
                        id: 4
                    }
                ]
            }
        }
    },
    {
        id: 902,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'Her & you',
            question: 'How often are you two together?',
            answers: [
                {
                    text: 'Most weeks',
                    id: 1
                },
                {
                    text: 'Once a month or so',
                    id: 2
                },
                {
                    text: 'A few times a year',
                    id: 3
                },
                {
                    text: 'Hardly ever',
                    id: 4
                }
            ]
        }
    },
    {
        id: 903,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'Her & you',
            question: 'And when you do — what do you talk about?',
            answers: [
                {
                    text: 'Health, weather, routine tasks',
                    id: 4
                },
                {
                    text: 'Family news and gossip',
                    id: 5
                },
                {
                    text: 'The same 3 stories told again',
                    id: 6
                },
                {
                    text: 'Honestly — not much at all',
                    id: 7
                }
            ]
        }
    },
    {
        id: 9031,
        component: PAUSE_SLIDE,
        isNotIndexing: true,
        continueButton: {
            isVisible: true
        },
        props: {
            imageSrc: pauseCollage1Src,
            titlePrefix: 'You talk about everything.',
            titleAccent: 'Except her.',
            description: <>You know how she is this week. That's a different thing from <strong>knowing who she was.</strong> There's a whole person in there you've never been introduced to.</>
        }
    },
    {
        id: 904,
        component: QUESTION_MULTI_ANSWER_SLIDE_COMPONENT,
        continueButton: {
            isVisible: true
        },
        props: {
            category: 'Her & you',
            question: 'Which of these do you actually know?',
            description: 'Be honest. Most people tick one or two.',
            answers: [
                {
                    text: 'Who she wanted to be, before you',
                    icon: 'cap',
                    id: 1
                },
                {
                    text: 'If she was frightened as a new mother',
                    icon: 'new-mother',
                    id: 2
                },
                {
                    text: 'The hardest year she’s ever had',
                    icon: 'wave',
                    id: 3
                },
                {
                    text: 'Something she gave up — and whether she minded',
                    icon: 'ribbon',
                    id: 4
                },
                {
                    text: 'What she’s proud of that has nothing to do with you',
                    icon: 'trophy',
                    id: 5
                },
                {
                    text: 'Honestly — none of these',
                    icon: 'sad',
                    id: 6
                }
            ]
        }
    },
    {
        id: 9041,
        component: PAUSE_SLIDE,
        isNotIndexing: true,
        continueButton: {
            isVisible: true
        },
        props: {
            imageSrc: pauseCollage2Src,
            titlePrefix: 'It’s not because you',
            titleAccent: 'don’t love her.',
            description: <>{'We tell ourselves we’ll ask ‘someday’. But ‘someday’ is a lie. '}<strong>One day, there are no more tomorrows</strong>{' — only silence and regret for questions left unasked forever.'}</>
        }
    },
    {
        id: 905,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'Her & you',
            question: 'What happens when you try to ask about her past?',
            answers: [
                {
                    text: '"Oh, nobody wants to hear about my life"',
                    icon: 'monkey',
                    id: 4
                },
                {
                    text: 'She brushes it off and changes the subject',
                    icon: 'refresh',
                    id: 5
                },
                {
                    text: 'She tells the same safe story she always tells',
                    icon: 'hourglass',
                    id: 6
                },
                {
                    text: 'She starts to open up, but time runs out',
                    icon: 'blossom',
                    id: 7
                },
                {
                    text: 'Honestly — I’ve never brought it up',
                    icon: 'neutral',
                    id: 8
                }
            ]
        }
    },
    {
        id: 906,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'Her & you',
            question: 'Now think about her mother. Your grandmother.',
            description: 'What do you know?',
            answers: [
                {
                    text: 'A lot — I knew her well',
                    icon: 'heart',
                    id: 1
                },
                {
                    text: 'Two or three stories',
                    icon: 'puzzle',
                    id: 2
                },
                {
                    text: 'Her name and a face',
                    icon: 'worried',
                    id: 3
                },
                {
                    text: 'Almost nothing',
                    icon: 'question',
                    id: 4
                }
            ]
        }
    },
    {
        id: 9061,
        component: PAUSE_SLIDE,
        isNotIndexing: true,
        continueButton: {
            isVisible: true
        },
        props: {
            imageSrc: pauseCollage3Src,
            titlePrefix: 'That’s how',
            titleAccent: 'fast it goes',
            isTitleInline: true,
            description: <>Whatever you leave unasked dies with her. <strong>Your children will leave yours unasked too.</strong></>
        }
    },
    {
        id: 907,
        component: QUESTION_MULTI_ANSWER_SLIDE_COMPONENT,
        continueButton: {
            isVisible: true,
            minAnswers: 2
        },
        props: {
            category: 'Personalize your plan',
            question: 'What do you want to know about her?',
            description: 'Select at least 2',
            answers: PLAN_TOPIC_ANSWERS
        }
    },
    {
        id: 908,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'Personalize your plan',
            question: 'Which chapter should she start with?',
            answerSourceId: 907,
            answerPool: PLAN_TOPIC_ANSWERS
        }
    },
    {
        id: 9081,
        component: PRODUCT_SLIDE,
        isNotIndexing: true,
        continueButton: {
            isVisible: true
        }
    },
    {
        id: 909,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'Personalize your plan',
            question: 'Which would be easier for her?',
            answers: [
                {
                    text: 'Talking — she’d never sit and write',
                    icon: 'mic-old',
                    id: 4
                },
                {
                    text: 'Writing — she’d want the words right',
                    icon: 'writing',
                    id: 5
                },
                {
                    text: 'Depends on the day',
                    icon: 'question',
                    id: 6
                }
            ]
        }
    },
    {
        id: 9091,
        component: PRODUCT_SLIDE,
        isNotIndexing: true,
        continueButton: {
            isVisible: true
        },
        props: {
            variant: 'v2'
        }
    },
    {
        id: 9092,
        component: PLAN_GRAPH_SLIDE,
        isNotIndexing: true,
        continueButton: {
            isVisible: true,
            label: 'Start today'
        }
    },
    {
        id: 910,
        component: QUESTION_MULTI_ANSWER_SLIDE_COMPONENT,
        continueButton: {
            isVisible: true
        },
        props: {
            category: 'Personalize your plan',
            question: 'Who else should be able to hear this one day?',
            answers: [
                {
                    text: 'My children',
                    icon: 'children',
                    id: 1
                },
                {
                    text: 'My brothers and sisters',
                    icon: 'siblings',
                    id: 2
                },
                {
                    text: 'Grandchildren who aren’t born yet',
                    icon: 'baby',
                    id: 3
                },
                {
                    text: 'Her — I think she’d like to read it back',
                    icon: 'heart-red',
                    id: 4
                },
                {
                    text: 'Just me',
                    icon: 'smile',
                    id: 5
                }
            ]
        }
    },
    {
        id: 911,
        component: QUESTION_ANSWER_SLIDE_COMPONENT,
        props: {
            category: 'Personalize your plan',
            question: 'What will haunt you when she’s gone?',
            answers: [
                {
                    text: 'Her secrets that will die with her',
                    icon: 'candle',
                    id: 7
                },
                {
                    text: 'The stories my children will never get to hear',
                    icon: 'girl',
                    id: 8
                },
                {
                    text: 'Who she really was, once she’s gone forever',
                    icon: 'ribbon',
                    id: 9
                },
                {
                    text: 'That I had the time today — and chose to wait',
                    icon: 'hourglass',
                    id: 10
                },
                {
                    text: 'All of the above',
                    icon: 'broken-heart',
                    id: 11
                }
            ]
        }
    },
    {
        id: 944,
        component: PLAN_LOADER_SLIDE,
        isNotBackButton: true,
        isNotIndexing: true
    },
    {
        id: 945,
        component: EMAIL_SLIDE,
        isNotBackButton: true,
        isNotIndexing: true
    }
];

export {
    QUIZ_SLIDES_V27
};
